/**
 * `GmsBootScreen` — the **one** interstitial every loading / connecting / link-lost state in the
 * app renders. It's the `/login` terminal's twin: the shared {@link GmsScreen} desk, a centred
 * brutalist window over the dithered mat, and the same `.gms-card` + `.gms-titlebar` chrome the
 * login and desktop windows wear. Waiting on the omninet and signing in to it look like the same
 * booting Omnihook, because they are. Styles live in `gms-boot.css`; the brutalist vocabulary has
 * no `border-radius`.
 *
 * The call sites are the whole family: {@link RequireAuth}'s reclaim/reconnect hold, the home
 * router's GM and player roster waits (`routes/home/index.tsx`). Add a new one here rather than
 * hand-rolling a card.
 */
import type { ReactNode } from "react";
import { GmsScreen } from "./GmsScreen";
import "./gms-boot.css";

type GmsBootScreenProps = {
  /** The titlebar's right-hand chip — one all-caps word of terminal status (`LINK`, `RELINK`, `OFFLINE`). */
  status: string;
  /** The headline caption: what the machine is doing. */
  title: string;
  /** The dimmer second line: what that means for the visitor. */
  detail: string;
  /**
   * Whether the accent block pulses. `true` (the default) is the "still working" tell for a state
   * that's waiting on something; pass `false` for a *terminal* state (a closed link), where a
   * pulsing indicator would falsely imply the machine is still trying.
   */
  pulse?: boolean;
  /** Optional action row — e.g. the stand-down note's manual "return to desktop" affordance. */
  action?: ReactNode;
};

export function GmsBootScreen({ status, title, detail, pulse = true, action }: GmsBootScreenProps) {
  return (
    <GmsScreen>
      <div className="gms-boot-stage">
        <div className="gms-boot-window">
          <div className="gms-boot-mat" />
          <div className="gms-card">
            <div className="gms-titlebar">
              <span className="gms-boot-diamond">◆</span>
              <span className="gms-titlebar-name">OMNIHOOK TERMINAL</span>
              <div className="gms-titlebar-fill" />
              <span className="gms-boot-status">{status}</span>
            </div>
            <div className="gms-boot-body">
              <span className={pulse ? "gms-boot-dot gms-boot-dot--pulse" : "gms-boot-dot"} aria-hidden />
              <div className="gms-boot-lines">
                <h1 className="gms-boot-title">{title}</h1>
                <p className="gms-boot-detail">{detail}</p>
              </div>
            </div>
            {action && <div className="gms-boot-actions">{action}</div>}
          </div>
        </div>
      </div>
    </GmsScreen>
  );
}

/**
 * The anti-flash hold: what a loading state renders for the first ~300ms (see `useDelayedFlag`),
 * before it's earned a card. A bare field in the GMS desk colour — so a warm reconnect resolves
 * with no intermediate UI at all, and a slow one fades up a card on the same background rather
 * than swapping the page colour underneath it.
 */
export function GmsBootHold() {
  return <div className="gms-boot-hold" />;
}
